import {
  type AttributionTouch,
  readAttributionState,
} from '@/attribution/client';

const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const UTM_KEY_PATTERN = /^utm_[a-z0-9_]+$/;
const MAX_UTM_PARAMS = 20;
const MAX_VALUE_LENGTH = 500;

export function getOAuthCallbackUrlWithAttribution(callbackUrl: string) {
  if (typeof window === 'undefined') return callbackUrl;

  let storedTouch: AttributionTouch | undefined;
  try {
    storedTouch = readAttributionState(window.localStorage).touches.at(-1);
  } catch {
    // Current URL attribution still works when storage is unavailable.
  }

  return mergeAttributionIntoCallbackUrl({
    callbackUrl,
    currentUrl: new URL(window.location.href),
    storedTouch,
  });
}

export function mergeAttributionIntoCallbackUrl({
  callbackUrl,
  currentUrl,
  storedTouch,
}: {
  callbackUrl: string;
  currentUrl: URL;
  storedTouch?: AttributionTouch;
}) {
  try {
    const callback = new URL(callbackUrl, currentUrl.origin);
    if (callback.origin !== currentUrl.origin) return callbackUrl;

    mergeCurrentUrlParams(callback.searchParams, currentUrl.searchParams);
    mergeStoredTouch(callback.searchParams, storedTouch);

    if (isAbsoluteUrl(callbackUrl)) return callback.toString();
    return `${callback.pathname}${callback.search}${callback.hash}`;
  } catch {
    return callbackUrl;
  }
}

function mergeCurrentUrlParams(
  target: URLSearchParams,
  source: URLSearchParams
) {
  let utmCount = countUtmParams(target);
  for (const [rawKey, rawValue] of source) {
    const key = rawKey.trim().toLowerCase();
    if (isClickIdKey(key)) {
      setIfMissing(target, key, rawValue);
      continue;
    }
    if (!UTM_KEY_PATTERN.test(key) || utmCount >= MAX_UTM_PARAMS) continue;
    if (setIfMissing(target, key, rawValue)) utmCount += 1;
  }
}

function mergeStoredTouch(
  target: URLSearchParams,
  touch: AttributionTouch | undefined
) {
  if (!touch) return;

  for (const key of CLICK_ID_KEYS) {
    const value = touch[key];
    if (value) setIfMissing(target, key, value);
  }

  let utmCount = countUtmParams(target);
  for (const [key, value] of Object.entries(touch.utmParams)) {
    if (utmCount >= MAX_UTM_PARAMS) break;
    if (setIfMissing(target, key, value)) utmCount += 1;
  }
}

function setIfMissing(target: URLSearchParams, key: string, value: string) {
  const normalized = value.trim().slice(0, MAX_VALUE_LENGTH);
  if (!normalized || target.has(key)) return false;
  target.set(key, normalized);
  return true;
}

function countUtmParams(searchParams: URLSearchParams) {
  let count = 0;
  for (const key of searchParams.keys()) {
    if (UTM_KEY_PATTERN.test(key.toLowerCase())) count += 1;
  }
  return count;
}

function isClickIdKey(key: string): key is (typeof CLICK_ID_KEYS)[number] {
  return CLICK_ID_KEYS.includes(key as (typeof CLICK_ID_KEYS)[number]);
}

function isAbsoluteUrl(value: string) {
  return /^[a-z][a-z\d+.-]*:/i.test(value);
}
